import { Col, Row } from "react-grid-system"
import { NormalFormEmailField, NormalFormPhoneField, NormalFormTextField } from "../molecules"
import { useUserLanguage } from "@/customHooks"
import textsData from '@/textData/BankFormTexts.json'
import { useCallback, useRef, useState } from "react"
import { BankDataProps } from "@/custom-types"
import styled from "styled-components"

type BankFormControlledProps = {
  onChange: (address: BankDataProps) => void;
  readOnly?: boolean;
  defaultData?: BankDataProps;
}

type AccountNumberProps = {
  number1: string
  number2: string
}

const emptyData: BankDataProps = {
  name: '',
  accountHolderName: '',
  accountNumber: '',
  email: '',
  phone: '',
}

const WarnText = styled.p<{ $isHidden: boolean }>`
  color: ${({ theme }) => theme.colors.warn};
  font-size: ${({ theme }) => theme.typography.fontSize.s5};
  visibility: ${({ $isHidden }) => $isHidden ? 'hidden' : 'visible'};
  margin-left: 1rem;
`

export const BankFormControlled = ({ onChange, readOnly, defaultData }: BankFormControlledProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const bankDataRef = useRef<BankDataProps>(defaultData ?? emptyData)

  const accountNumber = useRef<AccountNumberProps>({ number1: '', number2: '' })

  const [isInvalidAccountNumber, setInvalidAccountNumber] = useState(false)

  const fieldChangeHandle = useCallback((field: keyof BankDataProps, value: string | null) => {
    bankDataRef.current = {
      ...bankDataRef.current,
      [field]: value ?? '',
    }
    onChange(bankDataRef.current)
  }, [onChange])

  const accountNumberChangeHandle = (field: keyof AccountNumberProps, value: string | null) => {
    accountNumber.current = {
      ...accountNumber.current,
      [field]: value ?? '',
    }

    if (accountNumber.current.number1 === accountNumber.current.number2) {
      bankDataRef.current = {
        ...bankDataRef.current,
        accountNumber: accountNumber.current.number1,
      }
      setInvalidAccountNumber(false)
    } else {
      bankDataRef.current = {
        ...bankDataRef.current,
        accountNumber: '',
      }
      setInvalidAccountNumber(true)
    }

    onChange(bankDataRef.current)
  }

  return (
    <>
      <Row>
        <Col md={6}>
          <NormalFormTextField
            label={texts.name}
            placeholder={texts.namePlaceholder}
            name="bankName"
            value={defaultData?.name}
            readOnly={readOnly}
            onChange={(value) => fieldChangeHandle('name', value)}
          />
        </Col>
        <Col md={6}>
          <NormalFormTextField
            label={texts.accountHolderName}
            placeholder={texts.accountHolderNamePlaceholder}
            name="bankAccountHolder"
            value={defaultData?.accountHolderName}
            readOnly={readOnly}
            onChange={(value) => fieldChangeHandle('accountHolderName', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormTextField
            label={texts.accountNumber}
            placeholder={texts.accountNumberPlaceholder}
            name="bankAccountNumber"
            value={defaultData?.accountNumber}
            readOnly={readOnly}
            onChange={(value) => accountNumberChangeHandle('number1', value)}
          />
        </Col>
        <Col md={6}>
          <NormalFormTextField
            label={texts.accountNumberConfirmation}
            placeholder={texts.accountNumberConfirmationPlaceholder}
            name="confirmbankAccountNumber"
            value={defaultData?.accountNumber}
            readOnly={readOnly}
            onChange={(value) => accountNumberChangeHandle('number2', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6} offset={{ md: 6 }}>
          <WarnText $isHidden={!isInvalidAccountNumber}>Los Números de cuenta deben ser iguales</WarnText>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormEmailField
            label={texts.email}
            placeholder={texts.emailPlaceholder}
            name="bankEmail"
            value={defaultData?.email}
            readOnly={readOnly}
            onChange={(value) => fieldChangeHandle('email', value)}
          />
        </Col>
        <Col md={6}>
          <NormalFormPhoneField
            label={texts.phoneNumber}
            placeholder={texts.phoneNumberPlaceholder}
            name="bankPhoneNumber"
            value={defaultData?.phone}
            readOnly={readOnly}
            onChange={(value) => fieldChangeHandle('phone', value)}
          />
        </Col>
      </Row>
    </>
  )
}